import { apiClient } from './api';
import {
  Fahrzeug,
  FahrzeugUpdateRequest,
  FahrzeugPage,
  FahrzeugCreateData,
  Kennzeichen,
  KennzeichenCreateRequest,
  KennzeichenUpdateRequest,
  Verwertungsnachweis,
  VerwertungsnachweisRequest
} from '../types/fahrzeug';

export class FahrzeugService {
  private static readonly BASE_URL = '/fahrzeuge';

  static async updateFahrzeug(id: number, updateData: FahrzeugUpdateRequest): Promise<Fahrzeug> {
    const response = await apiClient.put(`${this.BASE_URL}/${id}`, updateData);
    return response.data;
  }

  static async getFahrzeugById(id: number): Promise<Fahrzeug> {
    const response = await apiClient.get(`${this.BASE_URL}/${id}`);
    return response.data;
  }

  static async getFahrzeugByZulassungsantragId(zulassungsantragId: number): Promise<Fahrzeug> {
    const response = await apiClient.get(`${this.BASE_URL}/zulassungsantrag/${zulassungsantragId}`);
    return response.data;
  }

  static async createFahrzeug(fahrzeugData: FahrzeugCreateData): Promise<Fahrzeug> {
    const response = await apiClient.post(this.BASE_URL, fahrzeugData);
    return response.data;
  }

  static async deleteFahrzeug(id: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${id}`);
  }

  static async getAlleFahrzeuge(page: number = 0, size: number = 20): Promise<FahrzeugPage> {
    const response = await apiClient.get(`${this.BASE_URL}?page=${page}&size=${size}`);
    return response.data;
  }

  static async getFahrzeugeByHersteller(hersteller: string): Promise<Fahrzeug[]> {
    const response = await apiClient.get(`${this.BASE_URL}/hersteller/${hersteller}`);
    return response.data;
  }

  static async createNeuesKennzeichen(fahrzeugId: number, data: KennzeichenCreateRequest): Promise<Kennzeichen> {
    const response = await apiClient.post(`${this.BASE_URL}/${fahrzeugId}/neues-kennzeichen`, data);
    return response.data;
  }

  static async updateNeuesKennzeichen(
    fahrzeugId: number,
    kennzeichenId: number,
    data: KennzeichenUpdateRequest
  ): Promise<Kennzeichen> {
    const response = await apiClient.put(
      `${this.BASE_URL}/${fahrzeugId}/neues-kennzeichen/${kennzeichenId}`,
      data
    );
    return response.data;
  }

  static async deleteNeuesKennzeichen(fahrzeugId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${fahrzeugId}/neues-kennzeichen`);
  }

  // Altes Kennzeichen (generisch - für nicht AUSSERBETRIEBSETZUNG)
  static async createAltesKennzeichen(fahrzeugId: number, data: KennzeichenCreateRequest): Promise<Kennzeichen> {
    const response = await apiClient.post(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen`, data);
    return response.data;
  }

  static async updateAltesKennzeichen(
    fahrzeugId: number,
    kennzeichenId: number,
    data: KennzeichenUpdateRequest
  ): Promise<Kennzeichen> {
    const response = await apiClient.put(
      `${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/${kennzeichenId}`,
      data
    );
    return response.data;
  }

  static async deleteAltesKennzeichen(fahrzeugId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen`);
  }

  // Altes Kennzeichen Vorn (für AUSSERBETRIEBSETZUNG)
  static async createAltesKennzeichenVorn(fahrzeugId: number, data: KennzeichenCreateRequest): Promise<Kennzeichen> {
    const response = await apiClient.post(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/vorn`, data);
    return response.data;
  }

  static async getAltesKennzeichenVorn(fahrzeugId: number): Promise<Kennzeichen> {
    const response = await apiClient.get(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/vorn`);
    return response.data;
  }

  static async updateAltesKennzeichenVorn(
    fahrzeugId: number,
    kennzeichenId: number,
    data: KennzeichenUpdateRequest
  ): Promise<Kennzeichen> {
    const response = await apiClient.put(
      `${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/vorn/${kennzeichenId}`,
      data
    );
    return response.data;
  }

  static async deleteAltesKennzeichenVorn(fahrzeugId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/vorn`);
  }

  // Altes Kennzeichen Hinten (für AUSSERBETRIEBSETZUNG)
  static async createAltesKennzeichenHinten(fahrzeugId: number, data: KennzeichenCreateRequest): Promise<Kennzeichen> {
    const response = await apiClient.post(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/hinten`, data);
    return response.data;
  }

  static async getAltesKennzeichenHinten(fahrzeugId: number): Promise<Kennzeichen> {
    const response = await apiClient.get(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/hinten`);
    return response.data;
  }

  static async updateAltesKennzeichenHinten(
    fahrzeugId: number,
    kennzeichenId: number,
    data: KennzeichenUpdateRequest
  ): Promise<Kennzeichen> {
    const response = await apiClient.put(
      `${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/hinten/${kennzeichenId}`,
      data
    );
    return response.data;
  }

  static async deleteAltesKennzeichenHinten(fahrzeugId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${fahrzeugId}/altes-kennzeichen/hinten`);
  }

  // Verwertungsnachweis (für Außerbetriebsetzung mit Fahrzeugverwertung)
  static async getVerwertungsnachweis(fahrzeugId: number): Promise<Verwertungsnachweis> {
    const response = await apiClient.get(`${this.BASE_URL}/${fahrzeugId}/verwertungsnachweis`);
    return response.data;
  }

  static async updateVerwertungsnachweis(
    fahrzeugId: number,
    data: VerwertungsnachweisRequest
  ): Promise<Verwertungsnachweis> {
    const response = await apiClient.put(
      `${this.BASE_URL}/${fahrzeugId}/verwertungsnachweis`,
      data
    );
    return response.data;
  }

  static async deleteVerwertungsnachweis(fahrzeugId: number): Promise<void> {
    await apiClient.delete(`${this.BASE_URL}/${fahrzeugId}/verwertungsnachweis`);
  }
}
